import React, { useMemo, useState } from 'react';
import '../styles/CalendarioEscolar.css';

const roles = [
  { id: 'admin', label: 'Admin' },
  { id: 'professor', label: 'Professor' },
  { id: 'aluno', label: 'Aluno' }
];

const meses = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
];

const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const eventosIniciais = [
  {
    id: 1,
    data: '2026-02-05',
    titulo: 'Reunião de abertura',
    descricao: 'Boas-vindas ao ano letivo.',
    tipo: 'Institucional'
  },
  {
    id: 2,
    data: '2026-02-18',
    titulo: 'Avaliação diagnóstica',
    descricao: 'Aplicação para todas as turmas.',
    tipo: 'Avaliação'
  },
  {
    id: 3,
    data: '2026-03-10',
    titulo: 'Semana da Leitura',
    descricao: 'Atividades de incentivo à leitura.',
    tipo: 'Projeto'
  }
];

const tiposEvento = ['Avaliação', 'Reunião', 'Feriado', 'Projeto', 'Institucional'];

const CalendarDay = ({ day, isToday, isCurrentMonth, eventos, onSelect, isSelected }) => {
  return (
    <button
      className={`calendario-dia ${isCurrentMonth ? '' : 'fora-mes'} ${isToday ? 'hoje' : ''} ${isSelected ? 'selecionado' : ''}`}
      onClick={() => onSelect(day.date)}
      type="button"
    >
      <span>{day.number}</span>
      {eventos.length > 0 && (
        <div className="marcadores">
          {eventos.slice(0, 3).map((evento) => (
            <span key={evento.id} className="marcador" title={evento.titulo}></span>
          ))}
        </div>
      )}
    </button>
  );
};

const CalendarioEscolar = () => {
  const [role, setRole] = useState('admin');
  const [mesAtual, setMesAtual] = useState(() => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  });
  const [dataSelecionada, setDataSelecionada] = useState(() => {
    const hoje = new Date();
    return hoje.toISOString().split('T')[0];
  });
  const [eventos, setEventos] = useState(eventosIniciais);
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipo, setTipo] = useState(tiposEvento[0]);

  const isAdmin = role === 'admin';

  const dataFormatada = useMemo(() => {
    if (!dataSelecionada) return '';
    const [ano, mes, dia] = dataSelecionada.split('-');
    return `${dia}/${mes}/${ano}`;
  }, [dataSelecionada]);

  const eventosDoDia = useMemo(() => {
    return eventos.filter((evento) => evento.data === dataSelecionada);
  }, [eventos, dataSelecionada]);

  const calendarioDias = useMemo(() => {
    const year = mesAtual.getFullYear();
    const month = mesAtual.getMonth();
    const primeiroDia = new Date(year, month, 1);
    const ultimoDia = new Date(year, month + 1, 0);
    const startDay = primeiroDia.getDay();
    const totalDays = ultimoDia.getDate();

    const dias = [];

    for (let i = 0; i < startDay; i += 1) {
      const date = new Date(year, month, i - startDay + 1);
      dias.push({
        date: date.toISOString().split('T')[0],
        number: date.getDate(),
        isCurrentMonth: false
      });
    }

    for (let day = 1; day <= totalDays; day += 1) {
      const date = new Date(year, month, day);
      dias.push({
        date: date.toISOString().split('T')[0],
        number: day,
        isCurrentMonth: true
      });
    }

    while (dias.length % 7 !== 0) {
      const date = new Date(year, month + 1, dias.length - totalDays - startDay + 1);
      dias.push({
        date: date.toISOString().split('T')[0],
        number: date.getDate(),
        isCurrentMonth: false
      });
    }

    return dias;
  }, [mesAtual]);

  const navegarMes = (direction) => {
    const novoMes = new Date(mesAtual);
    novoMes.setMonth(novoMes.getMonth() + direction);
    setMesAtual(novoMes);
  };

  const handleAdicionarEvento = () => {
    if (!titulo.trim()) {
      alert('Informe um título para o evento.');
      return;
    }

    const novoEvento = {
      id: Date.now(),
      data: dataSelecionada,
      titulo: titulo.trim(),
      descricao: descricao.trim(),
      tipo
    };

    setEventos((prev) => [novoEvento, ...prev]);
    setTitulo('');
    setDescricao('');
  };

  const handleExcluirEvento = (id) => {
    setEventos((prev) => prev.filter((evento) => evento.id !== id));
  };

  const handleEditarEvento = (id) => {
    const evento = eventos.find((item) => item.id === id);
    if (!evento) return;

    const novoTitulo = prompt('Editar título do evento:', evento.titulo);
    if (novoTitulo === null) return;

    const novaDescricao = prompt('Editar descrição do evento:', evento.descricao || '');
    if (novaDescricao === null) return;

    setEventos((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              titulo: novoTitulo.trim() || item.titulo,
              descricao: novaDescricao.trim()
            }
          : item
      )
    );
  };

  return (
    <div className="calendario-escolar">
      <header className="calendario-header">
        <div>
          <h2>Calendário Escolar</h2>
          <p>Visualize e gerencie as datas mais importantes do ano letivo.</p>
        </div>
        <div className="role-selector">
          <span>Perfil ativo</span>
          <div className="role-options">
            {roles.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`role-pill ${role === item.id ? 'active' : ''}`}
                onClick={() => setRole(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <section className="calendario-grid">
        <div className="calendario-painel">
          <div className="calendario-topo">
            <button type="button" className="nav-btn" onClick={() => navegarMes(-1)}>
              ◀
            </button>
            <div className="mes-atual">
              <span>{meses[mesAtual.getMonth()]}</span>
              <strong>{mesAtual.getFullYear()}</strong>
            </div>
            <button type="button" className="nav-btn" onClick={() => navegarMes(1)}>
              ▶
            </button>
          </div>

          <div className="dias-semana">
            {diasSemana.map((dia) => (
              <span key={dia}>{dia}</span>
            ))}
          </div>

          <div className="dias-mes">
            {calendarioDias.map((day) => {
              const eventosDia = eventos.filter((evento) => evento.data === day.date);
              const hoje = new Date().toISOString().split('T')[0] === day.date;
              return (
                <CalendarDay
                  key={day.date}
                  day={day}
                  isCurrentMonth={day.isCurrentMonth}
                  isToday={hoje}
                  eventos={eventosDia}
                  onSelect={setDataSelecionada}
                  isSelected={dataSelecionada === day.date}
                />
              );
            })}
          </div>
        </div>

        <div className="calendario-detalhes">
          <div className="detalhes-header">
            <div>
              <h3>Eventos em {dataFormatada}</h3>
              <p>{isAdmin ? 'Admin pode adicionar, editar ou excluir.' : 'Somente visualização.'}</p>
            </div>
            <span className={`role-badge ${role}`}>{role}</span>
          </div>

          {isAdmin && (
            <div className="evento-form">
              <h4>Novo evento</h4>
              <div className="form-group">
                <label htmlFor="titulo">Título</label>
                <input
                  id="titulo"
                  type="text"
                  value={titulo}
                  onChange={(event) => setTitulo(event.target.value)}
                  placeholder="Digite o título..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="tipo">Categoria</label>
                <select id="tipo" value={tipo} onChange={(event) => setTipo(event.target.value)}>
                  {tiposEvento.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="descricao">Descrição</label>
                <textarea
                  id="descricao"
                  value={descricao}
                  onChange={(event) => setDescricao(event.target.value)}
                  placeholder="Detalhes do evento..."
                />
              </div>
              <button type="button" className="btn-primario" onClick={handleAdicionarEvento}>
                Adicionar data importante
              </button>
            </div>
          )}

          <div className="eventos-lista">
            {eventosDoDia.length === 0 ? (
              <div className="empty-state">
                <p>Sem eventos cadastrados para esta data.</p>
              </div>
            ) : (
              eventosDoDia.map((evento) => (
                <article key={evento.id} className="evento-card">
                  <div>
                    <h4>{evento.titulo}</h4>
                    <span className="evento-tipo">{evento.tipo}</span>
                    {evento.descricao && <p>{evento.descricao}</p>}
                  </div>
                  {isAdmin && (
                    <div className="evento-acoes">
                      <button type="button" onClick={() => handleEditarEvento(evento.id)}>
                        Editar
                      </button>
                      <button type="button" onClick={() => handleExcluirEvento(evento.id)}>
                        Excluir
                      </button>
                    </div>
                  )}
                </article>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CalendarioEscolar;
